import { PointCloudOctree } from '@pnext/three-loader';
import { atom } from 'jotai';
import type { SavedViewCamera } from '../lib/savedViews';
import { atomFamily, atomWithStorage, createJSONStorage } from 'jotai/utils';
import * as THREE from 'three';

export type ViewerState = {
  imageId: string | null;
  googleImageId: string | null;
  activeBaseMapId: string | null;
  showGoogleStreetView: boolean;
  position: {
    x: number;
    y: number;
    z: number;
  };
};

export const DEFAULT_VIEWER_POSITION = {
  x: 0,
  y: 0,
  z: 0,
} as const;

// Type for the project's initial position (GeometryZ from GraphQL)
type GeometryZ = {
  coords: [number, number, number];
  type: string;
} | null;

const createDefaultState = (initialPosition?: { x: number; y: number; z: number }): ViewerState => ({
  imageId: null,
  googleImageId: null,
  activeBaseMapId: null,
  showGoogleStreetView: false,
  position: initialPosition ? { ...initialPosition } : { ...DEFAULT_VIEWER_POSITION },
});

// Helper function to extract position from project's initialPosition (GeometryZ)
const extractInitialPosition = (initialPosition: GeometryZ) => {
  if (!initialPosition || !initialPosition.coords) return null;
  const [x, y, z] = initialPosition.coords;
  return { x, y, z: z || 0 };
};

const storage = createJSONStorage<ViewerState>(() => sessionStorage);

// Store to keep track of project initial positions
const projectInitialPositions = new Map<string, { x: number; y: number; z: number }>();

export const setProjectInitialPosition = (projectId: string, initialPosition: GeometryZ) => {
  const extractedPosition = extractInitialPosition(initialPosition);
  if (extractedPosition) {
    projectInitialPositions.set(projectId, extractedPosition);
  }
};

// Function to update viewer state atom with initial position if currently at default
export const updateViewerStateWithInitialPosition = (
  projectId: string,
  setViewerState: (updater: (prev: ViewerState) => ViewerState) => void,
) => {
  const projectInitialPos = projectInitialPositions.get(projectId);
  if (projectInitialPos) {
    setViewerState((prev) => {
      const isDefaultPosition =
        prev.position.x === DEFAULT_VIEWER_POSITION.x &&
        prev.position.y === DEFAULT_VIEWER_POSITION.y &&
        prev.position.z === DEFAULT_VIEWER_POSITION.z;

      if (isDefaultPosition) {
        return {
          ...prev,
          position: projectInitialPos,
        };
      }
      return prev;
    });
  }
};

export const viewerStateAtomFamily = atomFamily((projectId: string) => {
  const storageKey = `@actegon.gis.viewer.${projectId}`;

  // Custom storage that checks for project initial position on first read
  const customStorage = {
    ...storage,
    getItem: (key: string, defaultValue: ViewerState) => {
      try {
        const stored = sessionStorage.getItem(key);
        if (stored) {
          const parsedData = JSON.parse(stored) as ViewerState;

          // Backward compat: fields may not exist in stored data
          if (parsedData.activeBaseMapId === undefined) {
            parsedData.activeBaseMapId = null;
          }
          if (parsedData.googleImageId === undefined) {
            parsedData.googleImageId = null;
          }
          if (parsedData.showGoogleStreetView === undefined) {
            parsedData.showGoogleStreetView = false;
          }

          // Check if stored position is default and we have a project initial position
          const projectInitialPos = projectInitialPositions.get(projectId);
          const isDefaultPosition =
            parsedData.position.x === DEFAULT_VIEWER_POSITION.x &&
            parsedData.position.y === DEFAULT_VIEWER_POSITION.y &&
            parsedData.position.z === DEFAULT_VIEWER_POSITION.z;

          if (isDefaultPosition && projectInitialPos) {
            // Update with project initial position
            const updatedData = {
              ...parsedData,
              position: projectInitialPos,
            };
            // Save the updated data back to storage
            try {
              sessionStorage.setItem(key, JSON.stringify(updatedData));
            } catch {
              // Ignore storage errors
            }
            return updatedData;
          }

          return parsedData;
        }

        // No stored data, check if we have project initial position
        const projectInitialPos = projectInitialPositions.get(projectId);
        if (projectInitialPos) {
          return createDefaultState(projectInitialPos);
        }

        return defaultValue;
      } catch {
        // Parse error, check if we have project initial position
        const projectInitialPos = projectInitialPositions.get(projectId);
        if (projectInitialPos) {
          return createDefaultState(projectInitialPos);
        }
        return defaultValue;
      }
    },
  };

  const persistedAtom = atomWithStorage<ViewerState>(storageKey, createDefaultState(), customStorage);

  return atom(
    (get) => get(persistedAtom),
    (get, set, updater: Partial<ViewerState> | ((prev: ViewerState) => ViewerState)) => {
      const previous = get(persistedAtom);
      const nextValue =
        typeof updater === 'function'
          ? (updater as (prev: ViewerState) => ViewerState)(previous)
          : {
              ...previous,
              ...updater,
              position: updater.position ?? previous.position,
            };

      set(persistedAtom, nextValue);
    },
  );
});

// Add Point Mode state management
export type AddPointMode = {
  enabled: boolean;
  targetLayerId: string | null;
  draftFeature: {
    geometry: { type: 'Point'; coordinates: [number, number, number] };
    properties: Record<string, any>;
  } | null;
};

export const addPointModeAtom = atom<AddPointMode>({
  enabled: false,
  targetLayerId: null,
  draftFeature: null,
});

// Add Surface Mode state management — flat reference polygon on a VECTOR layer.
// Two flavours, distinguished by `polygonMode`:
//
//  • Rectangle (default, polygonMode=false): exactly 3 picks → rectangle with full
//    edge / corner / center / normal drag handles. The original UX. Best for
//    flat walls, slabs, roofs where the user wants snap-to-rectangle.
//
//  • Polygon (polygonMode=true): N picks (≥3), terminated by an explicit
//    Finalize button. Each click appends a vertex; Undo pops the last one.
//    Adjust phase shows the closed outline + a single centre translate handle
//    (per-vertex drag is intentionally not in v1 — keeps the change small).
export type AddSurfacePhase =
  | 'picking-1'
  | 'picking-2'
  | 'picking-3'
  // Polygon mode only — entered after the third pick when polygonMode is on.
  // Stays here until the user clicks Finalize on the toolbar (or Undo back to
  // 'picking-3'). Each subsequent click appends another vertex.
  | 'picking-n'
  | 'adjusting';

export type AddSurfaceMode = {
  enabled: boolean;
  targetLayerId: string | null;
  phase: AddSurfacePhase;
  pickedPoints: Array<[number, number, number]>;
  // Length is 4 for rectangles (legacy [A, B, C, D]) and N≥3 for polygons.
  draftCorners: Array<[number, number, number]> | null;
  // Set when re-editing an existing surface feature; OK calls feature.onSave instead of createFeature.
  editFeatureId: string | null;
  // When true, the AB edge (first two clicks) is forced horizontal: B's Z is snapped
  // to A's Z before building the rectangle. The plane can still tilt around AB based
  // on click 3, so sloped reference planes remain expressible.
  horizontalLock: boolean;
  // When true, the rectangle becomes a perfectly vertical wall: AB horizontal AND
  // the v-axis aligned with world up. Click 3's Z determines wall height; its XY
  // is ignored. Implies horizontalLock for AB.
  verticalLock: boolean;
  // When true, picking continues past 3 clicks until the user hits Finalize.
  // Cleared by reset(). Locked once picking starts so the user can't switch
  // mid-flow (would invalidate the partial state).
  polygonMode: boolean;
};

export const DEFAULT_ADD_SURFACE_MODE: AddSurfaceMode = {
  enabled: false,
  targetLayerId: null,
  phase: 'picking-1',
  pickedPoints: [],
  draftCorners: null,
  editFeatureId: null,
  horizontalLock: true,
  verticalLock: false,
  polygonMode: false,
};

export const addSurfaceModeAtom = atom<AddSurfaceMode>(DEFAULT_ADD_SURFACE_MODE);

// Add Line Mode — N-point picking that produces a LineString (1 part) or
// MultiLineString (≥2 parts). `parts` holds finalised disjoint strokes;
// `currentPart` is the in-progress stroke. Hitting "+ Part" pushes the
// current part into `parts` and resets `currentPart` to []. Finalize commits
// to the adjusting phase, where editing is delegated to GeoJson.tsx's
// VertexMover (which already supports both LineString and MultiLineString).
export type AddGeometryPhase = 'picking' | 'adjusting';
export type LinePart = Array<[number, number, number]>;

export type AddLineMode = {
  enabled: boolean;
  targetLayerId: string | null;
  phase: AddGeometryPhase;
  parts: LinePart[];
  currentPart: LinePart;
  editFeatureId: string | null;
};

export const DEFAULT_ADD_LINE_MODE: AddLineMode = {
  enabled: false,
  targetLayerId: null,
  phase: 'picking',
  parts: [],
  currentPart: [],
  editFeatureId: null,
};

export const addLineModeAtom = atom<AddLineMode>(DEFAULT_ADD_LINE_MODE);

// Add Polygon Mode — N-point picking with ring closure that produces a
// Polygon (1 part) or MultiPolygon (≥2 parts). Each part is one outer ring
// stored without the closing duplicate vertex; persistence appends it.
// `snapToFirst` is true while the cursor is hovering near the first vertex
// of `currentPart` AND `currentPart.length >= 3` — clicking in that state
// auto-closes the ring (equivalent to Finalize).
export type PolygonPart = Array<[number, number, number]>;

export type AddPolygonMode = {
  enabled: boolean;
  targetLayerId: string | null;
  phase: AddGeometryPhase;
  parts: PolygonPart[];
  currentPart: PolygonPart;
  snapToFirst: boolean;
  editFeatureId: string | null;
};

export const DEFAULT_ADD_POLYGON_MODE: AddPolygonMode = {
  enabled: false,
  targetLayerId: null,
  phase: 'picking',
  parts: [],
  currentPart: [],
  snapToFirst: false,
  editFeatureId: null,
};

export const addPolygonModeAtom = atom<AddPolygonMode>(DEFAULT_ADD_POLYGON_MODE);

/**
 * Write-only atom that resets every geometry-creation tool to its default
 * disabled state. Used by `useExclusiveCreationTool` to enforce that only
 * one of {point, surface, line, polygon} can be active at a time.
 */
export const resetAllCreationToolsAtom = atom(null, (_get, set) => {
  set(addPointModeAtom, { enabled: false, targetLayerId: null, draftFeature: null });
  set(addSurfaceModeAtom, { ...DEFAULT_ADD_SURFACE_MODE });
  set(addLineModeAtom, { ...DEFAULT_ADD_LINE_MODE });
  set(addPolygonModeAtom, { ...DEFAULT_ADD_POLYGON_MODE });
});

// Typed event emitter
class EventEmitter<T extends Record<string, unknown>> {
  private listeners = new Map<keyof T, Set<(payload: any) => void>>();

  on<K extends keyof T>(event: K, fn: (payload: T[K]) => void) {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)!.add(fn);
    return () => this.off(event, fn);
  }

  off<K extends keyof T>(event: K, fn: (payload: T[K]) => void) {
    this.listeners.get(event)?.delete(fn);
  }

  emit<K extends keyof T>(event: K, payload: T[K]) {
    this.listeners.get(event)?.forEach((fn) => fn(payload));
  }
}

type ViewerEvents = {
  jumpToTarget: { x: number; y: number; z: number; radius?: number };
  setCenterTarget: { x: number; y: number; z: number };
  applyCameraState: SavedViewCamera;
  /**
   * Point the camera at a specific pose. Unlike `applyCameraState` this
   * carries no ortho/zoom/third-person state — it is a "look at this from
   * here" request, so it applies immediately in whatever projection the user
   * is in (spec §11.3).
   */
  lookAt: { position: [number, number, number]; target: [number, number, number] };
};

export const viewerEvents = new EventEmitter<ViewerEvents>();

// Clip Box
export type ClipBoxMode = 'NONE' | 'CLIP_OUTSIDE' | 'CLIP_INSIDE' | 'HIGHLIGHT_INSIDE';
export type ClipBoxInteractionMode = 'EDIT' | 'VIEW';

export type ClipBoxState = {
  center: [number, number, number] | null;
  halfSize: [number, number, number];
  rotation: [number, number, number, number]; // quaternion xyzw
  clipMode: ClipBoxMode;
  interactionMode: ClipBoxInteractionMode;
  enabled: boolean;
};

const DEFAULT_CLIP_BOX_STATE: ClipBoxState = {
  center: null,
  halfSize: [1, 1, 1],
  rotation: [0, 0, 0, 1],
  clipMode: 'HIGHLIGHT_INSIDE',
  interactionMode: 'EDIT',
  enabled: false,
};

const clipBoxStorage = createJSONStorage<ClipBoxState>(() => sessionStorage);

export const clipBoxStateAtom = atomFamily((projectId: string) =>
  atomWithStorage<ClipBoxState>(`@actegon.gis.clipbox.${projectId}`, DEFAULT_CLIP_BOX_STATE, clipBoxStorage),
);

// Viewer layout
export type ViewerLayout = 'full' | 'horizontal' | 'vertical';

export const viewerLayoutAtom = atomWithStorage<ViewerLayout>(
  '@actegon.gis.viewerLayout',
  'full',
  createJSONStorage<ViewerLayout>(() => sessionStorage),
);

// Transient hover state — no persistence
export const hoverImageIdAtom = atomFamily((_projectId: string) => atom<string | null>(null));

// Camera state published by 3D viewer for MapView consumption — no persistence
export type CameraMapState = {
  position: [number, number, number]; // raw CRS coords from camera
  rotationDeg: number;
  fovBase: number;
};

export const cameraMapStateAtom = atomFamily((_projectId: string) =>
  atom<CameraMapState>({ position: [0, 0, 0], rotationDeg: 0, fovBase: 60 }),
);

// Meters-per-pixel for the orthographic camera; null when perspective is active.
// Drives the OrthoScaleBar overlay. Computed from the visible viewport (zoom +
// frustum height + canvas height) — see CLAUDE.md "Screen-space sized handles".
export const orthoMetersPerPixelAtom = atomFamily((_projectId: string) => atom<number | null>(null));

/** Loaded PointCloudOctree instances for measuring tools to pick against. */
export const pointCloudTargetsAtom = atom<PointCloudOctree[]>([]);

/** THREE.Object3D groups from IFC / LandXML that measuring tools should raycast against. */
export const meshPickTargetsAtom = atom<THREE.Object3D[]>([]);

/** Async pick function registered by IFC2 for fragment model raycasting.
 *  Takes (camera, mousePixel, domElement) and returns a world-space point or null. */
export type FragmentPickFn = (
  camera: THREE.Camera,
  mouse: THREE.Vector2,
  dom: HTMLElement,
) => Promise<THREE.Vector3 | null>;

export const fragmentPickFnAtom = atom<FragmentPickFn | null>(null);
